import { useState } from 'react';
import { ActionIcon, Button, Group, Menu, Popover, SegmentedControl, Stack, Text, TextInput } from '@mantine/core';
import { CalendarPlus, ChevronDown, Trash2 } from 'lucide-react';
import type { ReactElement } from 'react';
import type { RangePreset, SavedRange } from './InsightsTypes';

interface InsightsRangeControlProps {
    presets: RangePreset[];
    saved: SavedRange[];
    value: string;
    saving: boolean;
    onSelect: (rangeKey: string) => void;
    onSaveRange: (label: string, startDate: string, endDate: string) => Promise<void>;
    onDeleteRange: (id: string) => void;
}

/**
 * Range selector: preset windows (7/30/90d) in the panel, plus — only when the
 * user has saved custom ranges — a single "Custom" entry that opens them to
 * pick from (individual saved ranges are not shown as panel segments). The "Add"
 * popover creates a new custom range, which the service computes on save.
 */
export default function InsightsRangeControl({
    presets,
    saved,
    value,
    saving,
    onSelect,
    onSaveRange,
    onDeleteRange,
}: InsightsRangeControlProps): ReactElement {
    const [open, setOpen] = useState(false);
    const [label, setLabel] = useState('');
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');

    const canSave = start !== '' && end !== '' && start <= end && !saving;
    const isPreset = presets.some((p) => p.key === value);
    const customActive = saved.some((s) => `custom:${s.id}` === value);

    const handleSave = async (): Promise<void> => {
        if (!canSave) return;
        await onSaveRange(label.trim() === '' ? `${start} → ${end}` : label.trim(), start, end);
        setOpen(false);
        setLabel('');
        setStart('');
        setEnd('');
    };

    return (
        <Group gap="sm" wrap="wrap">
            <SegmentedControl
                size="xs"
                value={isPreset ? value : ''}
                onChange={onSelect}
                data={presets.map((p) => ({ label: p.label, value: p.key }))}
            />
            {saved.length > 0 && (
                <Menu position="bottom-end" width={220} withArrow shadow="md">
                    <Menu.Target>
                        <Button size="xs" variant={customActive ? 'filled' : 'default'} rightSection={<ChevronDown size={14} />}>
                            Custom
                        </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Label>Saved ranges</Menu.Label>
                        {saved.map((s) => (
                            <Menu.Item
                                key={s.id}
                                onClick={() => onSelect(`custom:${s.id}`)}
                                rightSection={
                                    <ActionIcon
                                        component="div"
                                        size="sm"
                                        variant="subtle"
                                        color="red"
                                        aria-label={`Delete ${s.label}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDeleteRange(s.id);
                                        }}
                                    >
                                        <Trash2 size={13} />
                                    </ActionIcon>
                                }
                            >
                                {s.label}
                            </Menu.Item>
                        ))}
                    </Menu.Dropdown>
                </Menu>
            )}
            <Popover opened={open} onChange={setOpen} position="bottom-end" width={300} withArrow shadow="md">
                <Popover.Target>
                    <Button size="xs" variant="light" leftSection={<CalendarPlus size={14} />} onClick={() => setOpen((o) => !o)}>
                        Add
                    </Button>
                </Popover.Target>
                <Popover.Dropdown>
                    <Stack gap="xs">
                        <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
                            Save a custom range
                        </Text>
                        <TextInput size="xs" label="Label" placeholder="e.g. Q2 pilot" value={label} onChange={(e) => setLabel(e.currentTarget.value)} />
                        <Group grow gap="xs">
                            <TextInput size="xs" type="date" label="Start" value={start} onChange={(e) => setStart(e.currentTarget.value)} />
                            <TextInput size="xs" type="date" label="End" value={end} onChange={(e) => setEnd(e.currentTarget.value)} />
                        </Group>
                        <Button size="xs" onClick={() => void handleSave()} disabled={!canSave} loading={saving}>
                            Save &amp; compute
                        </Button>
                    </Stack>
                </Popover.Dropdown>
            </Popover>
        </Group>
    );
}
